import { concat } from '@ember/helper';
import { currencyStringToNumber, generateRandomString } from '../../utils/format';
import { fn } from '@ember/helper';
import {
  getAllValuesForTasField,
  setTrackedTasFieldValues,
} from '../../utils/tuition-assistance/fields';
import Input from 'tio-ui/components/input';
import Button from 'tio-ui/components/button';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Drawer from 'tio-ui/components/drawer';
import inputmask from '../../modifiers/inputmask';
import { lowercase } from 'tio-ui/utilities';
import type { ReceivedScholarship } from '../../types/tuition-assistance';
import type { TASCourseModelFieldsSignature } from 'tio-data/data/resources/tas-course.type';
import type { TasApplicationExtension as TasApplication } from 'tio-data/data/resources/tas-application.ext';
import { PhGraduationCap, PhPlus, PhTrash } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';

interface AddScholarshipsDrawerSignature {
  Args: {
    isOpen: boolean;
    application: TasApplication;
    onClose: () => void;
    scholarshipIdToEdit?: string;
  };
  Element: HTMLDivElement;
}

export type Scholarship = {
  id?: string;
  scholarshipAmount: number;
  scholarshipName: string;
  error?: string;
};

export default class AddScholarshipsDrawerComponent extends Component<AddScholarshipsDrawerSignature> {
  @service declare store: Store;

  @tracked scholarships: Scholarship[] = [];

  // Modifier to load scholarships when drawer opens
  loadOnOpen = modifier(() => {
    if (this.args.isOpen) {
      this.loadExistingScholarships();
    }
  });

  // Modifier to focus the scholarship amount input if editing a specific scholarship
  focusIfEditing = modifier((element: HTMLElement, [scholarshipId]: [string | undefined]) => {
    if (this.args.scholarshipIdToEdit && scholarshipId === this.args.scholarshipIdToEdit) {
      // Use requestAnimationFrame to wait for the drawer to be painted
      // Then wait for any CSS transitions to complete
      let handled = false;

      const focusInput = () => {
        if (handled) return;
        handled = true;

        const input = element.querySelector('input');
        if (input) {
          // Scroll the element into view first
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus and select the input
          input.focus();
          input.select();
        }
      };

      // Listen for transitionend on the drawer container
      const drawerContainer = element.closest('[role="dialog"]');
      const onTransitionEnd = (event: Event) => {
        // Check if this is a transform or opacity transition (typical drawer animations)
        const transitionEvent = event as TransitionEvent;
        if (
          transitionEvent.propertyName === 'transform' ||
          transitionEvent.propertyName === 'opacity'
        ) {
          focusInput();
          drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
        }
      };

      if (drawerContainer) {
        drawerContainer.addEventListener('transitionend', onTransitionEnd);
      }

      // Fallback: If no transition fires within 250ms, focus anyway
      const fallbackTimer = setTimeout(() => {
        focusInput();
        drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
      }, 250);

      return () => {
        clearTimeout(fallbackTimer);
        drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
      };
    }
  });

  loadExistingScholarships() {
    const existingScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED' as keyof TASCourseModelFieldsSignature,
      this.args.application.fields as unknown as TASCourseModelFieldsSignature
    ) as ReceivedScholarship[];

    this.scholarships = existingScholarships
      .filter((scholarship): scholarship is ReceivedScholarship => scholarship !== null)
      .map((scholarship, index) => ({
        scholarshipName: scholarship.scholarshipName,
        scholarshipAmount: scholarship.scholarshipAmount,
        id: scholarship.scholarshipId || `scholarship-${index}`,
        error: '',
      }));
  }

  getErrorForInput(id: string = '', scholarships: Scholarship[]) {
    return scholarships.find((scholarship: Scholarship) => scholarship.id === id)?.error ?? '';
  }

  formatAmount(amount: number) {
    // Amount is stored in cents, convert to dollars for display
    return (amount / 100).toString();
  }

  get programTemplate() {
    return this.args.application.tasProgramInstance.tasProgramTemplate;
  }

  get scholarshipName() {
    return this.programTemplate.fields['SCHOLARSHIP_NAME'];
  }

  get scholarshipAmount() {
    return this.programTemplate.fields['SCHOLARSHIP_AMOUNT'];
  }

  get scholarshipReceived() {
    return this.programTemplate.fields['SCHOLARSHIPS_RECEIVED'];
  }

  addScholarship = () => {
    this.scholarships.push({
      scholarshipName: '',
      scholarshipAmount: 0,
      error: '',
      id: generateRandomString(),
    });
    this.scholarships = [...this.scholarships];
  };

  removeScholarship = (id: string = '') => {
    this.scholarships = this.scholarships.filter((scholarship) => scholarship.id !== id);
  };

  closeDrawer = () => {
    this.args.onClose();
  };

  setName = (index: number, value: string) => {
    this.scholarships[index]!.scholarshipName = value;
  };

  setAmount = (index: number, value: string) => {
    this.scholarships[index]!.scholarshipAmount = currencyStringToNumber(value);
  };

  checkForMissingFields = async () => {
    this.scholarships.forEach((scholarship) => {
      if (
        (scholarship.scholarshipName === '' && this.scholarshipName.required) ||
        (scholarship.scholarshipAmount === 0 && this.scholarshipAmount.required)
      ) {
        scholarship.error = 'A required field is missing';
      } else {
        scholarship.error = '';
      }
      this.scholarships = [...this.scholarships];
    });
  };

  saveScholarships = async () => {
    await this.checkForMissingFields();
    const hasErrors = this.scholarships.some((scholarship) => scholarship.error !== '');
    if (hasErrors) {
      return;
    }
    // Use setTrackedTasFieldValues (not an in-place `fields[...] = ` write): it
    // REASSIGNS `application.fields` so WarpDrive's cache registers the change
    // and includes it in the save PATCH. An in-place mutation is not tracked and
    // is silently dropped from `serializePatch`, so the scholarship never saves.
    const scholarshipValues: ReceivedScholarship[] = this.scholarships.map((scholarship) => ({
      scholarshipName: scholarship.scholarshipName,
      scholarshipAmount: scholarship.scholarshipAmount,
      scholarshipId: scholarship.id,
    }));
    setTrackedTasFieldValues(this.args.application, 'SCHOLARSHIPS_RECEIVED', scholarshipValues);
    await saveRecord(this.store, this.args.application);
    // Call onClose to notify parent component to reload scholarship data
    this.args.onClose();
  };

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @allowCloseButton={{false}}
      @size="lg"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{false}}
      {{this.loadOnOpen}}
    >
      <:header>
        <div class="tio-row-between">
          <div class="flex-1">
            <h2 class="tio-fg-primary tio-text-heading">
              {{t
                "common.tas.add_scholarships_drawer.add_scholarships"
                label=this.scholarshipReceived.label
              }}
            </h2>
            <p class="tio-fg-muted text-sm">
              {{t
                "common.tas.add_scholarships_drawer.fill_out_scholarship_below"
                label=(lowercase this.scholarshipReceived.label)
              }}
            </p>
          </div>
        </div>
      </:header>

      <:body>
        <div class="overflow-y-auto flex-1 px-1">
          {{#if this.scholarships.length}}
            <div class="space-y-4 mt-2">
              {{#each this.scholarships as |scholarship index|}}
                <div class="border tio-border-default rounded-xl p-4 relative">
                  <button
                    type="button"
                    class="absolute top-3 right-3 tio-fg-disabled hover:tio-fg-danger transition-colors"
                    {{on "click" (fn this.removeScholarship scholarship.id)}}
                    data-test-remove-scholarship
                  >
                    <PhTrash @size="18" />
                  </button>
                  <div class="tio-col pr-6">
                    <Input
                      data-test={{concat "scholarship-name-" index}}
                      @label={{this.scholarshipName.label}}
                      @value={{scholarship.scholarshipName}}
                      @onChange={{fn this.setName index}}
                      @isRequired={{this.scholarshipName.required}}
                    />
                    <div {{this.focusIfEditing scholarship.id}}>
                      <Input
                        data-test={{concat "scholarship-amount-" index}}
                        @label={{this.scholarshipAmount.label}}
                        @value={{this.formatAmount scholarship.scholarshipAmount}}
                        @onChange={{fn this.setAmount index}}
                        @isRequired={{this.scholarshipAmount.required}}
                        {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
                      />
                    </div>
                    {{#if (this.getErrorForInput scholarship.id this.scholarships)}}
                      <p class="tio-fg-danger text-sm -mt-1">{{this.getErrorForInput
                          scholarship.id
                          this.scholarships
                        }}</p>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
            <button
              type="button"
              class="mt-4 tio-text-sm-medium text-link tio-inline-row-extra-tight hover:underline"
              data-test-add-scholarship
              {{on "click" this.addScholarship}}
            >
              <PhPlus @size="16" />
              {{t
                "common.tas.add_scholarships_drawer.add_another_scholarship"
                label=this.scholarshipReceived.label
              }}
            </button>
          {{else}}
            <div class="tio-place-center py-16 text-center">
              <PhGraduationCap @size="40" class="tio-fg-disabled mb-3" />
              <h3 class="tio-text-base-semibold tio-fg-secondary mb-1">
                {{t
                  "common.tas.add_scholarships_drawer.no_scholarships_added"
                  label=this.scholarshipReceived.label
                }}
              </h3>
              <p class="text-sm tio-fg-muted mb-4 max-w-xs">
                {{t
                  "common.tas.add_scholarships_drawer.no_scholarships_added_info"
                  label=(lowercase this.scholarshipReceived.label)
                }}
              </p>
              <Button
                @intent="primary"
                data-test-add-scholarship
                {{on "click" this.addScholarship}}
              >
                <PhPlus @size="16" />
                {{t
                  "common.tas.add_scholarships_drawer.add_scholarship"
                  label=this.scholarshipReceived.label
                }}
              </Button>
            </div>
          {{/if}}
        </div>
      </:body>

      <:footer>
        <div class="tio-row-loose justify-start w-full">
          <Button
            @appearance="minimal"
            {{on "click" this.closeDrawer}}
            data-test-close-scholarship-drawer
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            {{on "click" this.saveScholarships}}
            data-test-save-scholarships
          >
            {{t "common.save"}}
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>
}
